import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatMonthLabel } from '../../utils/wealthTracking/chartDataTransformers';

const SnapshotTimeline = ({ snapshots, selectedMonth, onMonthSelect, isDark }) => {
  // Sort snapshots by month
  const sortedSnapshots = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));

  const currentIndex = sortedSnapshots.findIndex(s => s.month === selectedMonth);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < sortedSnapshots.length - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onMonthSelect(sortedSnapshots[currentIndex - 1].month);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onMonthSelect(sortedSnapshots[currentIndex + 1].month);
    }
  };

  if (sortedSnapshots.length === 0) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border p-4 ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className={`p-2 rounded-lg transition-colors ${
            canGoPrevious
              ? isDark
                ? 'hover:bg-gray-700 text-white'
                : 'hover:bg-gray-100 text-gray-900'
              : 'opacity-30 cursor-not-allowed text-gray-500'
          }`}
          title="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Month Selector */}
        <div className="flex-1 flex items-center justify-center gap-3">
          <Calendar className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
          <select
            value={selectedMonth}
            onChange={(e) => onMonthSelect(e.target.value)}
            className={`px-4 py-2 rounded-lg border font-semibold text-center ${
              isDark
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            {sortedSnapshots.map((snapshot) => (
              <option key={snapshot.month} value={snapshot.month}>
                {formatMonthLabel(snapshot.month)}
              </option>
            ))}
          </select>
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={!canGoNext}
          className={`p-2 rounded-lg transition-colors ${
            canGoNext
              ? isDark
                ? 'hover:bg-gray-700 text-white'
                : 'hover:bg-gray-100 text-gray-900'
              : 'opacity-30 cursor-not-allowed text-gray-500'
          }`}
          title="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Progress indicator */}
      <div className="mt-3 flex items-center gap-1">
        {sortedSnapshots.map((snapshot, index) => (
          <button
            key={snapshot.month}
            onClick={() => onMonthSelect(snapshot.month)}
            className={`flex-1 h-1.5 rounded-full transition-all ${
              snapshot.month === selectedMonth
                ? 'bg-blue-500'
                : isDark
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
            title={formatMonthLabel(snapshot.month)}
          />
        ))}
      </div>

      {/* Snapshot count */}
      <div className="mt-2 text-center">
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {currentIndex + 1} of {sortedSnapshots.length} snapshots
        </p>
      </div>
    </div>
  );
};

export default SnapshotTimeline;
